import { useState, useCallback, useEffect } from 'react';
import { nanoid } from 'nanoid';
import type { Filter } from '@/shared/ui/search/AdvancedSearch/types/filter.types';
import type { FilterGroup } from '@/shared/ui/search/AdvancedSearch/types/filter-group.type';
import type { SearchQuery } from '@/shared/ui/search/AdvancedSearch/types/query.types';

export function useFilterManagement(initialQuery?: SearchQuery) {
  const [query, setQuery] = useState<SearchQuery>(
    initialQuery || {
      filterGroups: [
        {
          id: nanoid(),
          logic: 'AND',
          filters: [
            {
              id: nanoid(),
              fieldName: '',
              operator: 'EQUALS',
              value: null,
            },
          ],
        },
      ],
    }
  );

  // initialQuery prop이 변경되면 내부 상태도 업데이트 (모드 전환 등)
  useEffect(() => {
    if (initialQuery) {
      setQuery(initialQuery);
    }
  }, [initialQuery]);

  const addFilterGroup = useCallback(() => {
    const newGroup: FilterGroup = {
      id: nanoid(),
      logic: 'AND',
      filters: [
        {
          id: nanoid(),
          fieldName: '',
          operator: 'EQUALS',
          value: null,
        },
      ],
    };

    setQuery((prev) => ({
      ...prev,
      filterGroups: [...prev.filterGroups, newGroup],
    }));
  }, []);

  const removeFilterGroup = useCallback((groupId: string) => {
    setQuery((prev) => {
      if (prev.filterGroups.length <= 1) {
        return prev;
      }

      return {
        ...prev,
        filterGroups: prev.filterGroups.filter((g) => g.id !== groupId),
      };
    });
  }, []);

  const addFilter = useCallback((groupId: string) => {
    const newFilter: Filter = {
      id: nanoid(),
      fieldName: '',
      operator: 'EQUALS',
      value: null,
    };

    setQuery((prev) => ({
      ...prev,
      filterGroups: prev.filterGroups.map((g) =>
        g.id === groupId ? { ...g, filters: [...g.filters, newFilter] } : g
      ),
    }));
  }, []);

  const removeFilter = useCallback((groupId: string, filterId: string) => {
    setQuery((prev) => ({
      ...prev,
      filterGroups: prev.filterGroups.map((g) =>
        g.id === groupId
          ? {
              ...g,
              filters: g.filters.filter((f) => f.id !== filterId),
            }
          : g
      ),
    }));
  }, []);

  const updateFilter = useCallback(
    (groupId: string, filterId: string, updates: Partial<Filter>) => {
      setQuery((prev) => ({
        ...prev,
        filterGroups: prev.filterGroups.map((g) =>
          g.id === groupId
            ? {
                ...g,
                filters: g.filters.map((f) => (f.id === filterId ? { ...f, ...updates } : f)),
              }
            : g
        ),
      }));
    },
    []
  );

  const resetQuery = useCallback(() => {
    setQuery({
      filterGroups: [
        {
          id: nanoid(),
          logic: 'AND',
          filters: [
            {
              id: nanoid(),
              fieldName: '',
              operator: 'EQUALS',
              value: null,
            },
          ],
        },
      ],
    });
  }, []);

  return {
    query,
    setQuery,
    addFilterGroup,
    removeFilterGroup,
    addFilter,
    removeFilter,
    updateFilter,
    resetQuery,
  };
}
